import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { createTrasportatore } from '../api/trasportatori';
import type { TrasportatoreCreatePayload } from '../api/trasportatori';
import { trasportatoreCreateSchema, type TrasportatoreCreateData } from '../schemas/trasportatore';

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

function FieldWrap({ label, error, required, children }: { label: string; error?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-2 border-b border-stone-mid last:border-0 items-start">
      <label className="text-xs text-ink-soft min-w-[160px] pt-2 flex-shrink-0">
        {label}{required && <span className="text-accent-danger ml-0.5">*</span>}
      </label>
      <div className="flex-1">
        {children}
        {error && <p className="text-xs text-accent-danger mt-0.5">{error}</p>}
      </div>
    </div>
  );
}

export default function CreaTrasportatorePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const { register, handleSubmit, setError, formState: { errors } } = useForm<TrasportatoreCreateData>({
    resolver: zodResolver(trasportatoreCreateSchema),
    defaultValues: {
      ragione_sociale: '', numero_iscrizione_anga: '', data_rilascio: '',
      categorie_classe: '', piva: '', referente: '', contatti: '',
    },
  });

  function buildPayload(d: TrasportatoreCreateData): TrasportatoreCreatePayload {
    return {
      ragione_sociale: d.ragione_sociale,
      numero_iscrizione_anga: d.numero_iscrizione_anga,
      data_rilascio: d.data_rilascio,
      categorie_classe: d.categorie_classe,
      piva: d.piva?.trim() || null,
      referente: d.referente?.trim() || null,
      contatti: d.contatti?.trim() || null,
    };
  }

  const onSubmit = async (data: TrasportatoreCreateData) => {
    setIsSaving(true);
    setGlobalError(null);
    try {
      const created = await createTrasportatore(buildPayload(data));
      navigate(`/trasportatori/${created.id}`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setError('piva', { message: t('anagrafiche.crea_trasportatore.piva_esistente') });
      } else {
        setGlobalError(t('anagrafiche.crea_trasportatore.errore_salvataggio'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 text-sm mb-6">
          <Link to="/trasportatori" className="text-sage hover:underline flex items-center gap-1">
            <ArrowLeft size={14} />
            {t('anagrafiche.trasportatori.titolo')}
          </Link>
          <span className="text-ink-faint">/</span>
          <span className="font-semibold text-ink-dark">{t('anagrafiche.crea_trasportatore.titolo')}</span>
        </div>

        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-mid bg-stone-bg">
            <h1 className="text-base font-semibold text-ink-dark">{t('anagrafiche.crea_trasportatore.titolo')}</h1>
            <p className="text-xs text-ink-soft mt-0.5">{t('anagrafiche.crea_trasportatore.sottotitolo')}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4" data-testid="form-crea-trasportatore">
            {globalError && (
              <div className="mb-4 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">
                {globalError}
              </div>
            )}

            <dl className="divide-y divide-stone-mid">
              <FieldWrap label={t('anagrafiche.campi.ragione_sociale')} required error={errors.ragione_sociale?.message}>
                <input className={inputCls} {...register('ragione_sociale')} data-testid="input-ragione-sociale" />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.crea_trasportatore.lbl_anga')} required error={errors.numero_iscrizione_anga?.message}>
                <input className={inputCls} {...register('numero_iscrizione_anga')} data-testid="input-anga" placeholder={t('anagrafiche.crea_trasportatore.ph_anga')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.data_rilascio')} required error={errors.data_rilascio?.message}>
                <input className={inputCls} type="date" {...register('data_rilascio')} data-testid="input-data-rilascio" />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.categorie_classe')} required error={errors.categorie_classe?.message}>
                <input className={inputCls} {...register('categorie_classe')} placeholder={t('anagrafiche.crea_trasportatore.ph_categorie')} data-testid="input-categorie-classe" />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.partita_iva')} error={errors.piva?.message}>
                <input className={inputCls} {...register('piva')} maxLength={11} placeholder={t('anagrafiche.crea_cliente.ph_piva')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.referente')} error={errors.referente?.message}>
                <input className={inputCls} {...register('referente')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.contatti')} error={errors.contatti?.message}>
                <input className={inputCls} {...register('contatti')} placeholder={t('anagrafiche.crea_trasportatore.ph_contatti')} />
              </FieldWrap>
            </dl>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-stone-mid">
              <button type="button" onClick={() => navigate('/trasportatori')}
                className="px-4 py-2 text-sm text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg transition-colors">
                {t('common.azioni.annulla')}
              </button>
              <button type="submit" disabled={isSaving} data-testid="btn-submit-trasportatore"
                className="px-5 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
                {isSaving ? t('anagrafiche.crea_trasportatore.salvataggio') : t('anagrafiche.crea_trasportatore.crea')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
